import { ChakraProvider } from "@chakra-ui/react";
import React, { useEffect, useState, lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import SideBar from "./components/common/sidebarHeader";
import Footer from "./components/footer/footer";
import SignIn from "./components/SignIn/SignIn";
import Register from "./components/SignIn/Register";
import MultiSteps from "./components/Survey/Multisteps";
import theme from "./styles/theme";
import NotFoundPage from "./admin/pages/NotFoundPage";
import { TimeDoctorProvider } from "./contexts/TimeDoctorContext";
// import ProtectedRoute from "./components/ProtectedRoute";

// Lazy load components for authenticated routes
const AdminDashboardPage = lazy(
  () => import("./admin/pages/AdminDashboardPage")
);
const Billers = lazy(() => import("./admin/pages/biller/Biller"));
const BillerProfile = lazy(
  () => import("./admin/pages/billerProfile/BillerProfile")
);
const ChatScreen = lazy(() => import("./admin/pages/ChatScreen"));
const Clients = lazy(() => import("./admin/pages/client/Client"));
const Jobs = lazy(() => import("./admin/pages/job/Job"));
const Overview = lazy(() => import("./admin/pages/profile"));
const ReportScreen = lazy(() => import("./admin/pages/report"));
const Support = lazy(() => import("./admin/pages/support/Support"));
const WorkLog = lazy(() => import("./admin/pages/worklog/WorkLog"));
const Task = lazy(() => import("./admin/pages/task/Task"));
const NotificationsMenu = lazy(
  () => import("./admin/pages/notification/NotificationsMenu")
);
const BillerReport = lazy(
  () => import("./admin/pages/billerReport/BillerReport")
);
const ProductionReport = lazy(
  () => import("./admin/pages/productionReport/ProductionReport")
);
const TasksReport = lazy(() => import("./admin/pages/TasksReports/TaskReport"));
const AllBillers = lazy(
  () => import("./admin/pages/allbillerProfile/AllBillers")
);
const Attendance = lazy(() => import("./admin/pages/attendance/Attendance"));
const TimeDoctorPage = lazy(() => import("./admin/pages/timeDoctor/TimeDoctorPage"));
const TimeDoctorReportsPage = lazy(
  () => import("./admin/pages/timeDoctor/TimeDoctorReportsPage")
);
const TimeDoctorReport = lazy(() => import("./admin/pages/timeDoctor/TimeDoctorReport"));

// Loading component for Suspense fallback
const Loading = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      backgroundColor: "#f7fafc", // Light gray for a smoother transition
    }}
  >
    Loading...
  </div>
);

export default function App() {
  const [authChecked, setAuthChecked] = useState(false); // Track if auth status is resolved
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sideBarWidth, setSideBarWidth] = useState("large");

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "isUserLoggedIn") {
        setIsLoggedIn(event.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Check auth status immediately
    const userIsLoggedIn = localStorage.getItem("isUserLoggedIn");
    setIsLoggedIn(userIsLoggedIn === "true");
    setAuthChecked(true); // Mark auth as checked after initial load

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const handleSidebarWidth = () => {
    setSideBarWidth((prevWidth) => (prevWidth === "small" ? "large" : "small"));
  };

  // Don't render anything until auth status is confirmed
  if (!authChecked) {
    return <Loading />;
  }

  return (
    <ChakraProvider theme={theme}>
      <Suspense fallback={<Loading />}>
        {isLoggedIn ? (
          <TimeDoctorProvider>
            <SideBar
              sideBarWidth={sideBarWidth}
              handleSidebarWidth={handleSidebarWidth}
            />
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route
                path="/dashboard"
                element={<AdminDashboardPage sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/billers"
                element={<Billers sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/jobs"
                element={<Jobs sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/clients"
                element={<Clients sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/biller/profile/:userId"
                element={<BillerProfile sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/billerprofile"
                element={<BillerProfile sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/biller/explore-profiles"
                element={<AllBillers sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/reports"
                element={<ReportScreen sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/biller-reports"
                element={<BillerReport sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/attendance"
                element={<Attendance sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/admin/time-doctor"
                element={<TimeDoctorPage sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/admin/time-doctor/reports"
                element={<TimeDoctorReportsPage sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/chat"
                element={
                  <ChatScreen
                    sideBarWidth={sideBarWidth}
                    handleSidebarWidth={handleSidebarWidth}
                  />
                }
              />
              <Route
                path="/resumechat"
                element={
                  <ChatScreen
                    sideBarWidth={sideBarWidth}
                    handleSidebarWidth={handleSidebarWidth}
                  />
                }
              />
              <Route
                path="/profile"
                element={<Overview sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/support"
                element={<Support sideBarWidth={sideBarWidth} />}
              />
              <Route path="/notifications" element={<NotificationsMenu />} />
              <Route
                path="/worklog"
                element={<WorkLog sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/tasks"
                element={<Task sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/tasks-report"
                element={<TasksReport sideBarWidth={sideBarWidth} />}
              />
              <Route
                path="/production-report"
                element={<ProductionReport sideBarWidth={sideBarWidth} />}
              />
              <Route path="*" element={<NotFoundPage />} />{" "}
              {/* Catch-all moved here */}
            </Routes>
            <Footer />
          </TimeDoctorProvider>
        ) : (
          <Routes>
            <Route path="/" element={<SignIn />} />
            <Route path="/register" element={<Register />} />
            <Route path="/survey" element={<MultiSteps />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        )}
      </Suspense>
    </ChakraProvider>
  );
}
